// src/components/ui/LikeButton.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { likeManager, listenToLikeEvent, LikeEventDetail } from '../../utils/likeManager';

interface LikeButtonProps {
  modelId: string;
  initialCount: number;
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  showCount?: boolean;
  disabled?: boolean;
}

/**
 * モデルのいいね機能を提供するボタンコンポーネント
 */
const LikeButton: React.FC<LikeButtonProps> = ({
  modelId,
  initialCount = 0,
  className = "",
  iconClassName = "",
  textClassName = "",
  showCount = true,
  disabled = false
}) => {
  const { user: currentUser } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [count, setCount] = useState(initialCount);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const isMountedRef = useRef(true);
  const userIdRef = useRef<string | null>(null);

  // コンポーネントのマウント/アンマウント管理
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ユーザーID変更の検出
  useEffect(() => {
    const newUserId = currentUser?.uid || null;

    if (userIdRef.current !== newUserId) {
      userIdRef.current = newUserId;
      setIsInitialized(false);
      setIsLiked(false);
      likeManager.setUser(newUserId);
    }
  }, [currentUser]);

  // いいね状態の初期化
  useEffect(() => {
    if (isInitialized || !modelId) return;

    const fetchLikeState = async () => {
      try {
        setIsProcessing(true);

        if (currentUser) {
          const liked = await likeManager.isLiked(modelId);

          if (isMountedRef.current) {
            setIsLiked(liked);

            // いいね中だがカウントが0なら1に設定
            if (liked && initialCount === 0) {
              setCount(1);
            }
          }
        }

        if (isMountedRef.current) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error(`モデル ${modelId} のいいね状態取得エラー:`, error);
      } finally {
        if (isMountedRef.current) {
          setIsProcessing(false);
        }
      }
    };

    fetchLikeState();
  }, [modelId, currentUser, initialCount, isInitialized]);

  // いいねイベントのリスニング
  useEffect(() => {
    const unsubscribe = listenToLikeEvent(modelId, (detail: LikeEventDetail) => {
      if (!isProcessing && isMountedRef.current) {
        setIsLiked(detail.liked);

        if (detail.count !== undefined) {
          setCount(detail.count);
        } else {
          // カウント情報がない場合は推測
          setCount(prevCount => detail.liked
            ? prevCount + 1
            : Math.max(0, prevCount - 1));
        }
      }
    });

    return unsubscribe;
  }, [modelId, isProcessing]);

  // いいね状態の切り替え
  const handleToggleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentUser) {
      // ログインを促すメッセージを表示する代わりに何もしない
      return;
    }

    if (isProcessing) return;

    try {
      setIsProcessing(true);

      // 楽観的UI更新
      const newLikedState = !isLiked;
      const newCount = newLikedState ? count + 1 : Math.max(0, count - 1);

      setIsLiked(newLikedState);
      setCount(newCount);

      // likeManagerを通じていいね状態を切り替え
      await likeManager.toggleLike(modelId);
    } catch (error) {
      console.error(`モデル ${modelId} のいいね切り替えエラー:`, error);

      // エラー時は元の状態に戻す
      if (isMountedRef.current) {
        setIsLiked(isLiked);
        setCount(count);
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  return (
    <button
      onClick={handleToggleLike}
      disabled={disabled || isProcessing || !currentUser}
      className={`flex items-center justify-center ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${isProcessing ? 'cursor-wait' : ''} ${className}`}
      aria-label={isLiked ? "いいねを取り消す" : "いいねする"}
      data-model-id={modelId}
    >
      <svg
        viewBox="0 0 24 24"
        className={`transition-colors duration-300 ${isLiked ? "text-pink-500 fill-current" : "text-gray-400 dark:text-gray-500"} ${isProcessing ? "animate-pulse" : ""} ${iconClassName}`}
        width="24"
        height="24"
        stroke="currentColor"
        strokeWidth={isLiked ? "0" : "1.5"}
        fill={isLiked ? "currentColor" : "none"}
      >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
      {showCount && <span className={`${textClassName}`}>{count}</span>}
    </button>
  );
};

export default LikeButton;
